/**
 * BboxOverlay (M11) — PDF 페이지 위에 필드별 bounding box 를 그리는 컴포넌트.
 *
 * 좌표 변환:
 *   - 입력: PNG 픽셀 (M7-B 의 200 DPI 와 동일 좌표계)
 *   - 출력: 부모 overlay 의 % 좌표 (overlay 가 img 와 정확히 동일 크기이므로
 *     줌/리사이즈에 자동으로 따라감)
 *
 * 인터랙션 정책 (M11 F3+F4 단계에서 본격 연결):
 *   - 평상시: 흐릿한 outline (보조적)
 *   - hovered: 연한 파란색
 *   - active (focus): 진한 파란색 + ring
 *   - 클릭/hover 이벤트는 부모 (PdfPreviewPanel → EditRecordModal) 로 전파
 *
 * 책임 외:
 *   - 매칭 실패 필드의 폼 라벨 배지 (F5 는 EditRecordModal 의 폼 측에서 처리)
 *   - 페이지 자동 전환 (active 한 필드의 page 가 current page 와 다른 경우 등 —
 *     향후 enhancement)
 */

import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";
import type { FieldBbox } from "@/lib/api/jobs";

import { EDITABLE_FIELDS, type EditableFieldKey } from "./fieldLabels";

interface Props {
  bboxes: Record<string, FieldBbox | null>;
  /** PNG natural width/height (M7-B 의 200 DPI 픽셀). */
  pageW: number;
  pageH: number;
  /** 현재 보고있는 페이지 (1-based). 다른 페이지의 bbox 는 표시하지 않음. */
  currentPage: number;
  /** focus 된 필드 — 가장 강한 강조. */
  activeFieldKey?: EditableFieldKey | null;
  /** hover 된 필드 — 임시 강조. */
  hoveredFieldKey?: EditableFieldKey | null;
  /** bbox 자체가 클릭되었을 때 (F6: 폼 필드 focus 로 점프). */
  onClick?: (key: EditableFieldKey) => void;
  /** bbox 위에 mouse enter/leave 시 (F4: 양방향 — overlay → 폼 강조). */
  onHover?: (key: EditableFieldKey | null) => void;
}

export default function BboxOverlay({
  bboxes,
  pageW,
  pageH,
  currentPage,
  activeFieldKey,
  hoveredFieldKey,
  onClick,
  onHover,
}: Props) {
  const { t } = useTranslation();

  if (pageW <= 0 || pageH <= 0) return null;

  return (
    <>
      {EDITABLE_FIELDS.map((meta) => {
        const b = bboxes[meta.key];
        if (!b || b.page !== currentPage) return null;

        const [x1, y1, x2, y2] = b.bbox;
        const left = (x1 / pageW) * 100;
        const top = (y1 / pageH) * 100;
        const width = ((x2 - x1) / pageW) * 100;
        const height = ((y2 - y1) / pageH) * 100;

        const isActive = activeFieldKey === meta.key;
        const isHovered = hoveredFieldKey === meta.key;

        return (
          <div
            key={meta.key}
            data-field={meta.key}
            role="button"
            tabIndex={-1}
            aria-label={t("preview.bbox.aria", { label: t(meta.labelI18nKey) })}
            title={t("preview.bbox.titleConfidence", {
              label: t(meta.labelI18nKey),
              confidence: Math.round(b.confidence * 100),
            })}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(meta.key);
            }}
            onMouseEnter={() => onHover?.(meta.key)}
            onMouseLeave={() => onHover?.(null)}
            className={cn(
              "pointer-events-auto absolute box-content cursor-pointer border-2 transition-colors duration-100",
              isActive
                ? "border-blue-500 bg-blue-500/15 shadow-md ring-2 ring-blue-300/50"
                : isHovered
                  ? "border-blue-400/80 bg-blue-300/10"
                  : "border-blue-300/40 bg-blue-300/[0.04] hover:border-blue-400/70",
            )}
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: `${width}%`,
              height: `${height}%`,
            }}
          />
        );
      })}
    </>
  );
}
